import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TextTemplatePicker from '../../../TextTemplatePicker';
import DocumentTemplateSelector from '../../../DocumentTemplateSelector';
import { apiFetch } from '../../../utils/api';
import { generateDocumentOutput } from '../../../utils/documentGeneration';
import { getCompanyVariables } from '../../../utils/templateVariables';
import jobStatuses from '../../../constants/job-statuses';
import JobAssignmentFields from '../../../components/JobAssignmentFields';

function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [editedJob, setEditedJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [showDocumentTemplateSelector, setShowDocumentTemplateSelector] = useState(false);
  const [showRaisePOModal, setShowRaisePOModal] = useState(false);
  const [shortageItems, setShortageItems] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [people, setPeople] = useState([]);
  const [assignPersonId, setAssignPersonId] = useState('');
  const [assignDate, setAssignDate] = useState('');
  const [assignTime, setAssignTime] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [statusColors, setStatusColors] = useState({});
  const [siteEquipment, setSiteEquipment] = useState([]);

  useEffect(() => {
    fetchJob();
    fetchJobCategories();
    fetchTemplates();
    fetchEngineers();
    fetchStatusColors();
  }, [id]);

  const equipmentSiteId = editedJob && editedJob.site_id;
  const linkedEquipmentId = editedJob && editedJob.equipment_id;
  useEffect(() => {
    let live = true;
    if (!equipmentSiteId) {
      setSiteEquipment([]);
      return undefined;
    }
    apiFetch(`/api/sites/${equipmentSiteId}/equipment?includeRemoved=1`)
      .then((res) => res.json())
      .then((data) => {
        if (!live) return;
        const rows = Array.isArray(data) ? data : [];
        const linkedId = Number(linkedEquipmentId);
        setSiteEquipment(rows.filter((item) => {
          if (!item) return false;
          const removed = item.deleted_at != null || item.removed_at != null || item.is_active === false;
          if (!removed) return true;
          return Number(item.id) === linkedId;
        }));
      })
      .catch(() => {
        if (live) setSiteEquipment([]);
      });
    return () => {
      live = false;
    };
  }, [equipmentSiteId, linkedEquipmentId]);

  const fetchStatusColors = async () => {
    try {
      const res = await apiFetch('/api/job-status-colors');
      const data = await res.json();
      setStatusColors(jobStatuses.colourLookup(data));
    } catch (err) {
      console.error('Error fetching status colours:', err);
    }
  };

  const fetchEngineers = async () => {
    try {
      const res = await apiFetch('/api/employees');
      const rows = await res.json();
      setPeople(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Error fetching engineers:', err);
      setPeople([]);
    }
  };

  const engineers = useMemo(() => {
    const engineersOnly = people.filter((person) => {
      if (person.is_engineer === true || person.is_engineer === 't') return true;
      const role = `${person.role || ''} ${person.job_title || ''}`.toLowerCase();
      return role.includes('engineer') || role.includes('technician');
    });
    const visible = engineersOnly.length ? engineersOnly.slice() : people.slice();
    const chosen = people.find((person) => String(person.id) === String(assignPersonId));
    if (chosen && !visible.some((person) => person.id === chosen.id)) visible.unshift(chosen);
    return visible;
  }, [people, assignPersonId]);

  const applyVisit = (data) => {
    const visit = data && data.assignment;
    setAssignPersonId(visit && visit.person_id ? String(visit.person_id) : '');
    setAssignDate(visit && visit.date ? String(visit.date).slice(0, 10) : '');
    setAssignTime(visit && visit.time ? visit.time : '');
  };

  const fetchTemplates = async () => {
    try {
      const res = await apiFetch('/api/templates?area=job');
      const data = await res.json();
      setTemplates(data);
      const defaultTemplate = data.find(t => t.is_default);
      setSelectedTemplate(defaultTemplate || data[0]);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchJob = async () => {
    try {
      const res = await apiFetch(`/api/jobs/${id}`);
      const data = await res.json();
      setJob(data);
      setEditedJob(data);
      applyVisit(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching job:', err);
      setLoading(false);
    }
  };

  const fetchJobCategories = async () => {
    try {
      const res = await apiFetch('/api/custom-lists?list_type=job_category');
      const data = await res.json();
      setJobCategories(data);
    } catch (err) {
      console.error('Error fetching job categories:', err);
    }
  };

  const handleSave = async () => {
    const hasEngineer = String(assignPersonId || '').trim() !== '';
    const hasDate = String(assignDate || '').trim() !== '';
    if (hasEngineer !== hasDate) {
      alert('Choose both an engineer and a date, or leave both blank.');
      return;
    }
    const savedStatus = jobStatuses.normalizeJobStatus(job.job_status) || jobStatuses.DEFAULT_JOB_STATUS;
    const nextStatus = jobStatuses.normalizeJobStatus(editedJob.job_status) || jobStatuses.DEFAULT_JOB_STATUS;
    if (nextStatus !== savedStatus && !statusReason.trim()) {
      alert('A reason is required to change the status by hand.');
      return;
    }
    setSaving(true);
    try {
      const body = { ...editedJob };
      delete body.assignment;
      delete body.items;
      if (nextStatus !== savedStatus) body.status_reason = statusReason.trim();
      else delete body.status_reason;
      const hadVisit = Boolean(job && job.assignment && job.assignment.person_id);
      if (hasEngineer || hadVisit) {
        body.assign_person_id = hasEngineer ? Number(assignPersonId) : '';
        body.assign_date = hasDate ? assignDate : '';
        body.assign_time = assignTime || (hasEngineer ? '08:00' : '');
      }
      const res = await apiFetch(`/api/jobs/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const updated = await res.json().catch(() => ({}));
      if (res.ok) {
        setJob(updated);
        setEditedJob(updated);
        applyVisit(updated);
        setStatusReason('');
        alert('Job updated successfully!');
      } else {
        alert(updated.error || 'Failed to update job');
      }
    } catch (err) {
      console.error('Error saving job:', err);
      alert('Error saving job');
    }
    setSaving(false);
  };

  const handlePrint = async () => {
    setShowDocumentTemplateSelector(true);
  };

  const handlePDF = async () => {
    setShowDocumentTemplateSelector(true);
  };

  const handleDocumentTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      const templateData = await prepareJobData(job);
      await generateDocumentOutput(template, action, templateData, `Job-${job.job_number || job.id}`, outputWindow);
      setShowDocumentTemplateSelector(false);
    } catch (error) {
      console.error('Document generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  const handleEmail = async () => {
    alert('Email functionality coming soon!');
  };

  const prepareJobData = async (jobData) => {
    let companySettings = {};
    try {
      const res = await apiFetch('/api/company-settings');
      companySettings = await res.json();
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }

    const companyAddressParts = [
      companySettings.address_line1,
      companySettings.address_line2,
      companySettings.city,
      companySettings.county,
      companySettings.postcode
    ].filter(Boolean);
    const companyAddress = companyAddressParts.join(', ');

    return {
      customer_name: jobData.customer_name || '',
      customer_address: jobData.customer_address || '',
      customer_email: jobData.customer_email || '',
      customer_phone: jobData.customer_phone || '',
      job_number: jobData.job_number || '',
      job_date: jobData.created_at ? new Date(jobData.created_at).toLocaleDateString() : '',
      job_status: jobData.job_status || '',
      site_address: jobData.site_address || '',
      description: jobData.description || '',
      notes: jobData.notes || '',

      // Company fields (consistent across all templates)
      ...getCompanyVariables(companySettings)
    };
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm(`Create Invoice from Job ${job.job_number}?`)) {
      return;
    }

    try {
      const res = await apiFetch(`/api/jobs/${id}/convert-to-invoice`, {
        method: 'POST'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        alert(`Invoice ${data.invoice_ref} created from Job ${job.job_number}!`);
        navigate(`/finance/invoices/${data.invoice_id}`);
      } else {
        alert(data.error || 'Failed to create invoice from job');
      }
    } catch (err) {
      console.error('Error converting job:', err);
      alert('Error creating invoice from job');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete job #${job.job_number}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const res = await apiFetch(`/api/jobs/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('Job deleted successfully!');
        navigate('/jobs');
      } else {
        alert('Failed to delete job');
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      alert('Error deleting job');
    }
    setDeleting(false);
  };

  if (loading || !job || !editedJob) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/jobs')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">Job #{job.job_number}</h1>
              {(() => {
                const badge = jobStatuses.appearance(job.job_status, statusColors);
                return (
                  <span
                    data-testid="job-status-badge"
                    data-status={badge.status}
                    className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-sm font-semibold ${badge.badgeClass}`}
                  >
                    <span aria-hidden="true">{badge.icon}</span>
                    {badge.label}
                  </span>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              {job.quote_number && (
                <button
                  onClick={() => navigate(`/ops/quotes/${job.quote_id_full}`)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
                >
                  📄 Quote: {job.quote_number}
                </button>
              )}
              {job.invoice_number && (
                <button
                  onClick={() => navigate(`/finance/invoices/${job.invoice_id_full}`)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
                >
                  💰 Invoice: {job.invoice_number}
                </button>
              )}
              {jobStatuses.isCompleted(job.job_status) && !job.invoice_number && (
                <button
                  onClick={handleConvertToInvoice}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
                >
                  💰 Create Invoice
                </button>
              )}
              <button
                onClick={() => navigate(`/purchase-orders/new?job_id=${id}&job_number=${job.job_number}&site_id=${job.site_id}`)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition text-sm shadow-sm"
              >
                📦 Create PO
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {deleting ? '⏳ Deleting...' : '🗑️ Delete'}
              </button>
              <div className="relative">
                <button
                  onClick={() => setShowTemplateMenu(!showTemplateMenu)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm flex items-center gap-1"
                >
                  📋 {selectedTemplate ? selectedTemplate.name : 'Template'} ▼
                </button>
                {showTemplateMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowTemplateMenu(false)}></div>
                    <div className="absolute right-0 mt-1 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                      {templates.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-500">No templates found</div>
                      ) : (
                        templates.map(template => (
                          <button
                            key={template.id}
                            onClick={() => {
                              setSelectedTemplate(template);
                              setShowTemplateMenu(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition first:rounded-t-lg last:rounded-b-lg ${
                              selectedTemplate?.id === template.id ? 'bg-gray-50' : ''
                            }`}
                          >
                            <div className="font-medium text-gray-900">{template.name}</div>
                            {template.is_default && <div className="text-xs text-blue-600">Default</div>}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
              >
                🖨️ Print
              </button>
              <button
                onClick={handlePDF}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
              >
                📄 PDF
              </button>
              <button
                onClick={handleEmail}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm"
              >
                ✉️ Email
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-gray-100 border-4 border-gray-400 shadow-[inset_6px_6px_12px_rgba(0,0,0,0.3),inset_-4px_-4px_8px_rgba(255,255,255,0.9)] overflow-auto max-h-[calc(100vh-135px)] min-h-[650px]">
          <div className="p-8 pb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Details</h2>

            {/* Customer & Site Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {job.customer_name && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm flex items-center">
                    <span className="text-blue-600 mr-2">👤</span> Customer
                  </h3>
                  <div className="text-sm space-y-1">
                    <div className="font-medium">{job.customer_name}</div>
                    {job.customer_contact && <div className="text-gray-600">Contact: {job.customer_contact}</div>}
                    {job.customer_phone && <div className="text-gray-600">Phone: {job.customer_phone}</div>}
                    {job.customer_email && <div className="text-gray-600">Email: {job.customer_email}</div>}
                    {job.customer_address && (
                      <div className="text-gray-600">
                        {[job.customer_address, job.customer_city, job.customer_county, job.customer_postcode].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {job.site_name && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 text-sm flex items-center">
                    <span className="text-green-600 mr-2">📍</span> Site
                  </h3>
                  <div className="text-sm space-y-1">
                    <div className="font-medium">{job.site_name}</div>
                    {job.site_contact_name && <div className="text-gray-600">Contact: {job.site_contact_name}</div>}
                    {job.site_phone && <div className="text-gray-600">Phone: {job.site_phone}</div>}
                    {job.site_street && (
                      <div className="text-gray-600">
                        {[job.site_street, job.site_city, job.site_county, job.site_postcode].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <section className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="job-powra">
              <h3 className="font-semibold text-gray-900 mb-2">POWRA</h3>
              {!job.powra || job.powra.length === 0 ? <p className="text-sm text-gray-600">None recorded.</p> : (
                <ul className="space-y-3">
                  {job.powra.map((form) => {
                    const words = { proceed: 'Proceed', modify: 'Modify and proceed', stop: 'Stop and escalate' };
                    const when = form.completedAt
                      ? new Date(form.completedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : '';
                    return (
                      <li key={form.id} className="text-sm text-gray-800">
                        <p><span className="font-semibold">Engineer:</span> {form.engineer || 'Engineer'}</p>
                        <p><span className="font-semibold">Date:</span> {when}</p>
                        <p><span className="font-semibold">Decision:</span> {words[form.decision] || form.decision}</p>
                        {form.unexpectedNote ? <p><span className="font-semibold">New hazard:</span> {form.unexpectedNote}</p> : null}
                        {form.controlsNote ? <p><span className="font-semibold">Controls note:</span> {form.controlsNote}</p> : null}
                        <p><span className="font-semibold">Signed:</span> {form.signed ? 'Yes' : 'No'}</p>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-5">
                <div>
                  <label className="field-label mb-2 block">Job Number *</label>
                  <input
                    type="text"
                    value={editedJob?.job_number || ''}
                    onChange={(e) => setEditedJob({...editedJob, job_number: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value font-mono text-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                {job.quote_number && (
                  <div>
                    <label className="field-label mb-2 block">Quote Number</label>
                    <div
                      onClick={() => navigate(`/ops/quotes/${job.quote_id_full}`)}
                      className="w-full bg-green-50 border-2 border-green-300 rounded px-3 py-2 data-value font-mono text-lg cursor-pointer hover:bg-green-100 transition flex items-center justify-between"
                    >
                      <span className="text-green-700 font-semibold">{job.quote_number}</span>
                      <span className="text-green-600 text-sm">📄 View Quote →</span>
                    </div>
                  </div>
                )}

                {job.invoice_number && (
                  <div>
                    <label className="field-label mb-2 block">Invoice Number</label>
                    <div
                      onClick={() => navigate(`/finance/invoices/${job.invoice_id_full}`)}
                      className="w-full bg-blue-50 border-2 border-blue-300 rounded px-3 py-2 data-value font-mono text-lg cursor-pointer hover:bg-blue-100 transition flex items-center justify-between"
                    >
                      <span className="text-blue-700 font-semibold">{job.invoice_number}</span>
                      <span className="text-blue-600 text-sm">💰 View Invoice →</span>
                    </div>
                  </div>
                )}

                <div className="md:col-span-2">
                  <JobAssignmentFields
                    personId={assignPersonId}
                    date={assignDate}
                    time={assignTime}
                    engineers={engineers}
                    onPerson={(value) => {
                      setAssignPersonId(value);
                      if (value && !assignTime) setAssignTime('08:00');
                    }}
                    onDate={setAssignDate}
                    onTime={setAssignTime}
                  />
                </div>

                <div>
                  <label className="field-label mb-2 block">Job Status</label>
                  <select
                    value={jobStatuses.normalizeJobStatus(editedJob?.job_status) || jobStatuses.DEFAULT_JOB_STATUS}
                    onChange={(e) => setEditedJob({...editedJob, job_status: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                  >
                    {jobStatuses.JOB_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {(jobStatuses.normalizeJobStatus(editedJob?.job_status) || jobStatuses.DEFAULT_JOB_STATUS)
                    !== (jobStatuses.normalizeJobStatus(job.job_status) || jobStatuses.DEFAULT_JOB_STATUS) && (
                    <textarea
                      data-testid="job-status-reason"
                      aria-label="Reason for the status change"
                      value={statusReason}
                      onChange={(e) => setStatusReason(e.target.value)}
                      rows={2}
                      placeholder="Reason for changing the status"
                      className="w-full mt-2 bg-gray-50 border-2 border-gray-300 rounded px-3 py-2"
                    />
                  )}
                </div>

                <div>
                  <label className="field-label mb-2 block">Job Category</label>
                  <select
                    value={editedJob?.job_category || ''}
                    onChange={(e) => setEditedJob({...editedJob, job_category: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select category...</option>
                    {jobCategories.map(cat => (
                      <option key={cat.id} value={cat.list_value}>
                        {cat.list_value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </option>
                    ))}
                    <option value="service">Service</option>
                  </select>
                </div>

                <div>
                  <label className="field-label mb-2 block">PO Number</label>
                  <input
                    type="text"
                    value={editedJob?.po_number || ''}
                    onChange={(e) => setEditedJob({...editedJob, po_number: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="Purchase Order Number"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="field-label block">Job Description</label>
                    <TextTemplatePicker
                      category="Jobs"
                      onInsert={(text) => {
                        const current = editedJob?.job_description || '';
                        const newText = current ? current + '\n\n' + text : text;
                        setEditedJob({...editedJob, job_description: newText});
                      }}
                    />
                  </div>
                  <textarea
                    value={editedJob?.job_description || ''}
                    onChange={(e) => setEditedJob({...editedJob, job_description: e.target.value})}
                    rows="4"
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="Describe the work to be done..."
                  />
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-5">
                <div>
                  <label className="field-label mb-2 block">Door / gate</label>
                  <select
                    value={editedJob?.equipment_id || ''}
                    onChange={(e) => {
                      const nextId = e.target.value ? Number(e.target.value) : null;
                      const equip = siteEquipment.find((item) => Number(item.id) === nextId);
                      setEditedJob({
                        ...editedJob,
                        equipment_id: nextId,
                        door_type: equip ? equip.equipment_type : editedJob.door_type,
                      });
                    }}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">No door linked</option>
                    {siteEquipment.map((equip) => {
                      const removed = equip.deleted_at != null || equip.removed_at != null || equip.is_active === false;
                      const label = [
                        equip.location_description || equip.equipment_type,
                        [equip.manufacturer, equip.model].filter(Boolean).join(' '),
                      ].filter(Boolean).join(' — ');
                      return (
                        <option key={equip.id} value={equip.id}>
                          {removed ? `${label} (Removed)` : label}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-sm text-gray-600 mt-2">
                    Door / gate location: {(() => {
                      const linked = siteEquipment.find((item) => Number(item.id) === Number(editedJob?.equipment_id));
                      if (!linked || !linked.location_description) return 'Not stored';
                      const removed = linked.deleted_at != null || linked.removed_at != null || linked.is_active === false;
                      return removed ? `${linked.location_description} (Removed)` : linked.location_description;
                    })()}
                  </p>
                </div>

                <div>
                  <label className="field-label mb-2 block">Site Address</label>
                  <textarea
                    value={editedJob?.site_address || ''}
                    onChange={(e) => setEditedJob({...editedJob, site_address: e.target.value})}
                    rows="3"
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="Full site address..."
                  />
                </div>

                <div>
                  <label className="field-label mb-2 block">Site Contact</label>
                  <input
                    type="text"
                    value={editedJob?.site_contact || ''}
                    onChange={(e) => setEditedJob({...editedJob, site_contact: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="Contact name"
                  />
                </div>

                <div>
                  <label className="field-label mb-2 block">Site Number</label>
                  <input
                    type="tel"
                    value={editedJob?.site_number || ''}
                    onChange={(e) => setEditedJob({...editedJob, site_number: e.target.value})}
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="Phone number"
                  />
                </div>

                <div>
                  <label className="field-label mb-2 block">Opening Times / Restrictions</label>
                  <textarea
                    value={editedJob?.opening_times || ''}
                    onChange={(e) => setEditedJob({...editedJob, opening_times: e.target.value})}
                    rows="3"
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="e.g., Mon-Fri 9am-5pm, No access after 6pm..."
                  />
                </div>

                <div>
                  <label className="field-label mb-2 block">Specific Requirements</label>
                  <textarea
                    value={editedJob?.specific_requirements || ''}
                    onChange={(e) => setEditedJob({...editedJob, specific_requirements: e.target.value})}
                    rows="4"
                    className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-orange-500"
                    placeholder="PPE, parking, security clearance, etc..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-[116px] z-10">
        <div className="max-w-full mx-auto px-6 flex items-center gap-1">
          <button
            className="px-4 py-3 text-sm font-medium border-b-2 border-blue-600 text-blue-700"
          >
            Job Details
          </button>
          <button
            onClick={() => navigate(`/jobs/${id}/appointments`)}
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition"
          >
            Appointments
          </button>
          <button
            onClick={() => navigate(`/jobs/${id}/materials`)}
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition"
          >
            Materials
          </button>
          <button
            onClick={() => navigate(`/jobs/${id}/rams`)}
            className="px-4 py-3 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 transition"
          >
            RAMS
          </button>
        </div>
      </div>

      {/* Document Template Selector Modal */}
      <DocumentTemplateSelector
        isOpen={showDocumentTemplateSelector}
        onClose={() => setShowDocumentTemplateSelector(false)}
        documentType="job_sheet"
        placementKey="job.print_pdf"
        data={job}
        onGenerate={handleDocumentTemplateGenerate}
      />
    </div>
  );
}

export default JobDetail;
